import { useEffect, useMemo, useState } from 'react';
import { downloadFile, fetchFileBlob } from '../../api/boardContent';
import { BoardItem } from '../../types/boardContent';
import './BoardFilePreviewModal.css';

type PreviewKind = 'pdf' | 'image' | 'text' | 'unsupported';

interface Props {
  boardId: number;
  file: BoardItem;
  onClose: () => void;
}

function extOf(file: BoardItem) {
  const fromField = file.ext?.trim().toLowerCase();
  if (fromField) return fromField.replace(/^\./, '');
  const name = file.name || '';
  const dot = name.lastIndexOf('.');
  if (dot < 0) return '';
  return name.slice(dot + 1).toLowerCase();
}

function previewKind(ext: string): PreviewKind {
  if (ext === 'pdf') return 'pdf';
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg'].includes(ext)) return 'image';
  if (
    ['txt', 'md', 'csv', 'json', 'log', 'xml', 'html', 'htm', 'css', 'js', 'ts', 'tsx', 'jsx'].includes(
      ext
    )
  ) {
    return 'text';
  }
  return 'unsupported';
}

function BoardFilePreviewModal({ boardId, file, onClose }: Props) {
  const ext = useMemo(() => extOf(file), [file]);
  const kind = useMemo(() => previewKind(ext), [ext]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [textContent, setTextContent] = useState('');

  useEffect(() => {
    let objectUrlLocal: string | null = null;
    let cancelled = false;

    void (async () => {
      try {
        setLoading(true);
        setError('');
        setTextContent('');
        setObjectUrl(null);
        const blob = await fetchFileBlob(boardId, file.id);
        if (cancelled) return;

        if (kind === 'text') {
          const text = await blob.text();
          if (cancelled) return;
          setTextContent(text);
        } else if (kind === 'pdf' || kind === 'image') {
          const typed =
            kind === 'pdf'
              ? new Blob([blob], { type: 'application/pdf' })
              : blob.type
                ? blob
                : new Blob([blob], {
                    type: `image/${ext === 'jpg' ? 'jpeg' : ext || 'png'}`,
                  });
          objectUrlLocal = URL.createObjectURL(typed);
          if (cancelled) {
            URL.revokeObjectURL(objectUrlLocal);
            objectUrlLocal = null;
            return;
          }
          setObjectUrl(objectUrlLocal);
        }
      } catch {
        if (!cancelled) setError('파일을 불러오지 못했습니다.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrlLocal) URL.revokeObjectURL(objectUrlLocal);
    };
  }, [boardId, file.id, kind, ext]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="board-file-preview__backdrop" role="presentation" onClick={onClose}>
      <div
        className="board-file-preview"
        role="dialog"
        aria-modal="true"
        aria-labelledby="board-file-preview-title"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="board-file-preview__header">
          <div>
            <h3 id="board-file-preview-title">{file.name}</h3>
            <p>
              {(ext || '파일').toUpperCase()}
              {file.size != null ? ` · ${formatSize(file.size)}` : ''}
            </p>
          </div>
          <div className="board-file-preview__actions">
            <button
              type="button"
              className="board-file-preview__btn"
              onClick={() => void downloadFile(boardId, file.id, file.name)}
            >
              다운로드
            </button>
            <button type="button" className="board-file-preview__close" onClick={onClose}>
              닫기
            </button>
          </div>
        </header>

        <div className="board-file-preview__body">
          {loading ? <p className="board-file-preview__hint">불러오는 중…</p> : null}
          {error ? <p className="board-file-preview__error">{error}</p> : null}

          {!loading && !error && kind === 'pdf' && objectUrl ? (
            <iframe
              className="board-file-preview__frame"
              title={file.name}
              src={objectUrl}
            />
          ) : null}

          {!loading && !error && kind === 'image' && objectUrl ? (
            <div className="board-file-preview__image-wrap">
              <img src={objectUrl} alt={file.name} />
            </div>
          ) : null}

          {!loading && !error && kind === 'text' ? (
            <pre className="board-file-preview__text">{textContent || '(내용 없음)'}</pre>
          ) : null}

          {!loading && !error && kind === 'unsupported' ? (
            <div className="board-file-preview__unsupported">
              <p>이 형식은 브라우저에서 바로 미리볼 수 없습니다.</p>
              <p>다운로드 후 열어 주세요. (예: 엑셀, 워드, 한글 등)</p>
              <button
                type="button"
                className="board-file-preview__btn is-primary"
                onClick={() => void downloadFile(boardId, file.id, file.name)}
              >
                다운로드
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function formatSize(size: number) {
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

export default BoardFilePreviewModal;
